/**
 * Custom fetch mutator used by the Orval-generated client.
 * Same-origin requests: the dev preview and production server both serve
 * the API under /api, so no base URL is required.
 *
 * When authentication is enabled the frontend registers a token provider
 * (see setAuthTokenProvider); the mutator then attaches a bearer token to
 * every request. Tokens stay in memory — never persisted here.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export type AuthTokenProvider = () => Promise<string | null>;

export interface ApiEntityScope {
  tenantId: string;
  entityId: string;
}

export interface DocumentSaveRequest {
  url: string;
  method: string;
  body: RequestInit['body'];
  scope: ApiEntityScope;
  signal: AbortSignal;
}
export type DocumentSaveReviewer = (request: DocumentSaveRequest) => Promise<Record<string, string>>;
let documentSaveReviewer: DocumentSaveReviewer | null = null;

/** A single staff UI owner can review document destinations before a generated write. */
export function setDocumentSaveReviewer(reviewer: DocumentSaveReviewer | null): void {
  documentSaveReviewer = reviewer;
}

let tokenProvider: AuthTokenProvider | null = null;
let entityScope: ApiEntityScope | null = null;
let scopeController = new AbortController();
let portalContextId: string | null = null;
let portalContextController = new AbortController();

/** These identity/bootstrap requests do not depend on the selected business entity. */
export function isEntityIndependentRequest(url: string): boolean {
  const pathname = url.split('?')[0];
  return pathname === '/api/auth/me' || pathname === '/api/plugins/status' || pathname === '/api/access/contexts'
    || pathname === '/api/access/permissions/catalogue'
    || pathname?.startsWith('/api/platform/') === true || pathname?.startsWith('/api/me/') === true
    || pathname?.startsWith('/api/agent/') === true;
}

/** Portal requests are scoped independently from the signed-in staff entity selector. */
export function isPortalRequest(url: string): boolean {
  return url.split('?')[0]?.startsWith('/api/portal/') === true;
}

/** Register (or clear) the function used to obtain bearer tokens. */
export function setAuthTokenProvider(provider: AuthTokenProvider | null): void {
  tokenProvider = provider;
}

/** Set by the authenticated entity selector; never inferred from page input. */
export function setApiEntityScope(scope: ApiEntityScope | null): void {
  if (scope?.tenantId === entityScope?.tenantId && scope?.entityId === entityScope?.entityId) return;
  scopeController.abort();
  scopeController = new AbortController();
  entityScope = scope ? { ...scope } : null;
}

/** Set only from a context returned by /api/portal/me. */
export function setApiPortalContext(contextId: string | null): void {
  if (contextId === portalContextId) return;
  portalContextController.abort();
  portalContextController = new AbortController();
  portalContextId = contextId;
}

export function getApiPortalContext(): string | null {
  return portalContextId;
}

/** Included in TanStack's query hash so records cannot survive a scope switch. */
export function getApiEntityScopeCacheKey(): readonly string[] {
  return entityScope ? [entityScope.tenantId, entityScope.entityId] : ['unselected'];
}

/** Resolve the current bearer token, or null when auth is disabled. */
export async function getAuthToken(): Promise<string | null> {
  if (!tokenProvider) return null;
  try {
    return await tokenProvider();
  } catch {
    return null;
  }
}

async function buildHeaders(
  options: RequestInit,
  scope: ApiEntityScope | null,
  portalContext: string | null
): Promise<HeadersInit> {
  const isJsonBody = typeof options.body === 'string';
  const token = await getAuthToken();
  const headers = new Headers({
    Accept: 'application/json',
    ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(portalContext ? { 'X-Portal-Context': portalContext } : {})
  });
  if (scope) {
    headers.set('X-Tenant-Id', scope.tenantId);
    headers.set('X-Entity-Id', scope.entityId);
  }
  // A plain object merge kept both the scope's 'X-Tenant-Id' and an explicit
  // 'x-tenant-id' request header, and the Headers constructor comma-joined them
  // into one invalid value. set() applies per-request overrides
  // case-insensitively instead, and the server still re-authorizes the scope.
  if (options.headers) {
    new Headers(options.headers).forEach((value, key) => headers.set(key, value));
  }
  return headers;
}

/** Shared authenticated/scoped fetch boundary for generated JSON and typed streaming clients. */
export const scopedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const independent = isEntityIndependentRequest(url);
  const portal = isPortalRequest(url);
  const scope = independent ? null : entityScope;
  const signals = [
    options.signal,
    independent ? undefined : scopeController.signal,
    portal ? portalContextController.signal : undefined
  ].filter((signal): signal is AbortSignal => signal !== undefined);
  const signal = signals.length > 1 ? AbortSignal.any(signals) : signals[0];
  const destinationHeaders = !independent && !portal && scope && documentSaveReviewer
    ? await documentSaveReviewer({ url, method: options.method ?? 'GET', body: options.body, scope, signal: signal ?? scopeController.signal })
    : {};
  signal?.throwIfAborted();
  const headers = new Headers(await buildHeaders(options, scope, portal ? portalContextId : null));
  for (const [key, value] of Object.entries(destinationHeaders)) headers.set(key, value);
  signal?.throwIfAborted();
  const response = await fetch(url, {
    ...options,
    headers,
    signal
  });
  signal?.throwIfAborted();
  return response;
};

export const customFetch = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const independent = isEntityIndependentRequest(url);
  const signal = independent ? options.signal : options.signal
    ? AbortSignal.any([options.signal, scopeController.signal])
    : scopeController.signal;
  const response = await scopedFetch(url, { ...options, signal });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    const body = isJson
      ? ((await response.json().catch(() => undefined)) as
          | { error?: string; code?: string }
          | undefined)
      : undefined;
    signal?.throwIfAborted();
    if (body?.code === 'SCOPE_NOT_FOUND' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cdf:access-invalidated'));
    }
    throw new ApiError(
      body?.error ?? `Request failed with status ${response.status}`,
      response.status,
      body?.code
    );
  }

  if (!isJson) {
    // Binary endpoints (file download) resolve to a Blob.
    const body = await response.blob();
    signal?.throwIfAborted();
    return body as T;
  }

  const body = await response.json();
  signal?.throwIfAborted();
  return body as T;
};
